import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { RootState, AppDispatch } from "app/store";
import I18n from "common/i18n";
import { sleep } from "common/utils";
import { ParentGate } from "features/parent/ParentGate";
import { PlanSubTab } from "features/user/page/UserPageTab";
import { AVAILABLE_UPGRADE_PLANS } from "features/user/constants";
import {
  PlanCategory,
  BillingPeriod,
  usePlan,
  useUpgradePurchase,
  fetchAccountPlan,
} from "features/subscription";
import {
  UserPlanCurrentUsageCard,
  UserPlanUpgradePreviewCard,
  UserPlanUpgradeCta,
  UserPlanInactiveNotice,
  UserPlanWarningDialogs,
  GuestPurchaseOverlay,
} from "./components";

type Props = {
  subTab: PlanSubTab;
};

export const UserPlan = ({ subTab }: Props) => {
  const plan = usePlan();
  const upgrade = useUpgradePurchase();
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const user = useSelector((state: RootState) => state.user.parent);
  const activeUser = useSelector((state: RootState) => state.user.active);

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [parentGateVisible, setParentGateVisible] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<PlanCategory | null>(null);

  const availablePlans = AVAILABLE_UPGRADE_PLANS[plan] ?? [];
  const showCta = availablePlans.length > 0;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    if (!sessionId || !activeUser?.uid) return;

    handleDialog({ type: DialogType.LOADING, value: true });

    const syncAfterStripeRedirect = async () => {
      try {
        await sleep(3000);
        await dispatch(fetchAccountPlan({ uid: user.uid, forceSync: true })).unwrap();
        toast(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_USER_PLAN_PURCHASE_SUCCESS")}</p>
          </div>,
        );
      } catch (err) {
        console.error("Stripe redirect sync error:", err);
      } finally {
        handleDialog({ type: DialogType.LOADING, value: false });
        window.history.replaceState({}, "", "/user");
      }
    };
    syncAfterStripeRedirect();
  }, [activeUser?.uid, dispatch, handleDialog, user.uid]);

  const handleUpgradeClick = (category: PlanCategory) => {
    setPendingPurchase(category);
    setParentGateVisible(true);
  };

  const handleParentGateConfirm = () => {
    setParentGateVisible(false);
    if (pendingPurchase) {
      upgrade.purchase(pendingPurchase, billingPeriod);
      setPendingPurchase(null);
    }
  };

  const handleParentGateClose = () => {
    setParentGateVisible(false);
    setPendingPurchase(null);
  };

  const getPriceString = (category: PlanCategory, period: BillingPeriod): string =>
    upgrade.getPriceDisplay(category, period).priceString;

  const showPurchaseTab = subTab === PlanSubTab.PURCHASE && showCta;

  return (
    <div className="flex-col-el w-full h-full overflow-hidden">
      <div className="flex-col-el flex-1 min-h-0 w-full overflow-hidden">
        {showPurchaseTab ? (
          <GuestPurchaseOverlay>
            <div className="flex-col-el flex-1 min-h-0 w-full overflow-hidden p-4 gap-3">
              <UserPlanUpgradeCta
                currentPlan={plan}
                availablePlans={availablePlans}
                billingPeriod={billingPeriod}
                getPriceString={getPriceString}
                onChangeBillingPeriod={setBillingPeriod}
                onUpgrade={handleUpgradeClick}
                includeFreePlan
              />
            </div>
          </GuestPurchaseOverlay>
        ) : (
          <div className="flex-col-el flex-1 min-h-0 w-full overflow-hidden p-4 gap-3">
            {showCta ? (
              <div className="flex-row-el flex-1 min-h-0 gap-3 w-full">
                <div className="flex-col-el flex-[2] min-h-0 min-w-0">
                  <UserPlanCurrentUsageCard />
                </div>
                <div className="flex-col-el flex-[3] min-h-0 min-w-0">
                  <UserPlanUpgradePreviewCard availablePlans={availablePlans} />
                </div>
              </div>
            ) : (
              <>
                <UserPlanCurrentUsageCard />
                <UserPlanInactiveNotice plan={plan} />
              </>
            )}
          </div>
        )}

        <ParentGate
          visible={parentGateVisible}
          confirm={handleParentGateConfirm}
          close={handleParentGateClose}
        />

        <UserPlanWarningDialogs
          showVersionWarning={upgrade.showVersionWarning}
          showAmazonWarning={upgrade.showAmazonWarning}
          onCloseVersionWarning={upgrade.closeVersionWarning}
          onCloseAmazonWarning={upgrade.closeAmazonWarning}
          onGotoStoreUpdate={upgrade.gotoStoreUpdate}
        />
      </div>
    </div>
  );
};
